/**
 * Importes en € (enteros, sin céntimos en precio venta / propietario).
 * "88.000", "88.000 €" → 88000. No concatena decimales como dígitos extra.
 */
export function parsePrecioVentaEntero(raw) {
  if (raw == null || raw === "") return NaN;
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.round(raw);
  let s = String(raw)
    .trim()
    .replace(/€/gi, "")
    .replace(/\s/g, "");
  if (!s) return NaN;
  s = s.replace(/,(\d{1,2})\s*$/, "");
  s = s.replace(/\.(\d{1,2})\s*$/, "");
  s = s.replace(/[.,\s']/g, "");
  const n = parseInt(s, 10);
  return Number.isFinite(n) && n >= 0 ? n : NaN;
}

/**
 * Importe en € que puede llevar céntimos (honorarios, IVA, totales).
 * "6.611,57" → 6611.57 · "6611.57" → 6611.57 · "88.000" → 88000 (entero).
 */
export function parseEuroAmount(value) {
  if (value == null || value === "") return NaN;
  if (typeof value === "number" && Number.isFinite(value)) return value;

  let s = String(value)
    .trim()
    .replace(/€/gi, "")
    .replace(/\s/g, "");
  if (!s) return NaN;

  const decMatch = s.match(/^(.+)[,.](\d{1,2})$/);
  if (decMatch) {
    const intPart = decMatch[1].replace(/[^\d]/g, "") || "0";
    const frac = decMatch[2];
    const n = parseFloat(`${intPart}.${frac}`);
    return Number.isFinite(n) ? n : NaN;
  }

  return parsePrecioVentaEntero(s);
}

/**
 * @deprecated Usar parsePrecioVentaEntero (enteros) o parseEuroAmount (con céntimos).
 * Mantiene el nombre por compatibilidad; ya no concatena "8000.00" → 800000.
 */
export function parseMoneyDigits(value) {
  return parseEuroAmount(value);
}

/** Porcentajes con posible coma decimal (5,5 o 5.5). */
export function parsePercentFlexible(value) {
  const s = String(value ?? "")
    .replace(/\s/g, "")
    .replace(",", ".");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : NaN;
}

export function formatPrecioVentaEntero(n) {
  if (n == null || !Number.isFinite(n)) return "";
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(Math.round(n));
}

/** Precio venta / propietario con separador de miles y €. */
export function formatPrecioVentaConEuro(value) {
  if (value == null || value === "") return "";
  const n = typeof value === "number" ? value : parsePrecioVentaEntero(value);
  if (!Number.isFinite(n)) return String(value);
  return `${formatPrecioVentaEntero(n)} €`;
}

/** Formato € con separador de miles y 2 decimales (honorarios, IVA, totales). */
export function formatEuroAmount(n) {
  if (n == null || n === "") return "";
  const num = typeof n === "number" ? n : parseEuroAmount(n);
  if (!Number.isFinite(num)) return "";
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(num);
}

export function formatPercent2(value) {
  const n = parsePercentFlexible(value);
  if (!Number.isFinite(n)) return value == null ? "" : String(value);
  return n.toFixed(2);
}

/** Importe con separador de miles y símbolo € (visualización en inputs). */
export function formatEuroConSimbolo(value) {
  if (value == null || value === "") return "";
  const n = typeof value === "number" ? value : parseEuroAmount(value);
  if (!Number.isFinite(n)) return String(value);
  return `${new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(n)} €`;
}

export function roundEuro2(n) {
  return Math.round(Number(n) * 100) / 100;
}
